/**
 * EA PROD01 — Dynamisk prisvisning ved variant-valg
 * Lytter på 'on:variant:change' event fra variant-picker.js
 * Opdaterer: pris-label, ATC-knap tekst, størrelses-labels priser
 *
 * Implementeret: 29. juni 2026
 * Rettet v3: 29. juni 2026 — Bug fix: størrelses-labels priser synkroniseres
 *            korrekt ved skift af Serie (REGULAR/LONG) og Farve.
 *            Bruger window.appBlockPlacements[0].productVariants som variant-kilde.
 *            Labels: .ea-size-label / .ea-size-price-sub (korrekte klasser)
 *
 * Variant title format: "Farve / Størrelse / Serie"
 * Options: option1=Farve, option2=Størrelse, option3=Serie
 */

(function () {
  'use strict';

  // Formater pris fra Shopify cents til dansk format: "3.895 kr"
  function formatPrice(cents) {
    const amount = cents / 100;
    return amount.toLocaleString('da-DK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }) + ' kr';
  }

  // Formater pris som tal uden "kr" (til size-labels)
  function formatPriceNumber(cents) {
    const amount = cents / 100;
    return amount.toLocaleString('da-DK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  // Hent alle varianter fra appBlockPlacements (sat af Klarna app block i theme)
  function getAllVariants() {
    if (window.appBlockPlacements && window.appBlockPlacements[0] && window.appBlockPlacements[0].productVariants) {
      return window.appBlockPlacements[0].productVariants;
    }
    // Fallback: window.productVariants (sat af ea-prod01-price.js selv hvis tilgængeligt)
    if (window.productVariants) return window.productVariants;
    return [];
  }

  // Opdater ATC-knap tekst med pris
  function updateAtcButton(variant, section) {
    const atcBtn = section.querySelector(
      'button[name="add"][data-add-to-cart-text]'
    );
    if (!atcBtn) return;
    const baseText = atcBtn.dataset.addToCartText || 'TILFØJ TIL KURV';
    if (variant && variant.available) {
      const priceFormatted = formatPrice(variant.price);
      atcBtn.textContent = baseText + ' — ' + priceFormatted.toUpperCase();
    } else {
      atcBtn.textContent = baseText;
    }
  }

  // Opdater pris-label "· Størrelse XL"
  function updatePriceLabel(variant, section) {
    const label = section.querySelector('.ea-price-size-label');
    if (!label) return;
    if (variant) {
      // option2 er størrelse i "Farve / Størrelse / Serie" format
      const sizePart = variant.option2 || variant.title.split(' / ')[1] || variant.title;
      label.textContent = '· Størrelse ' + sizePart;
      label.hidden = false;
    } else {
      label.hidden = true;
    }
  }

  /**
   * Opdater priserne i ALLE størrelses-labels (.ea-size-label).
   *
   * Logik:
   * - Den aktive variant har option1 (Farve), option2 (Størrelse), option3 (Serie)
   * - For hver størrelses-label: find den variant der matcher
   *   label's størrelse (option2) + aktiv Farve (option1) + aktiv Serie (option3)
   * - Opdater .ea-size-price-sub tekst med den fundne variants pris
   */
  function updateSizeLabels(variant, section) {
    // Find alle størrelses-labels (ikke serie-labels)
    // Størrelses-labels er dem der har en for-attribut der peger på storrelse-option inputs
    const sizeLabels = section.querySelectorAll('.ea-size-label');
    if (!sizeLabels.length || !variant) return;

    const allVariants = getAllVariants();
    if (!allVariants.length) return;

    // Den aktive variants options
    const activeOption1 = variant.option1; // Farve
    const activeOption3 = variant.option3; // Serie (kan være null/undefined)

    sizeLabels.forEach(function (label) {
      // Find den tilhørende input for at få størrelses-værdien
      const forAttr = label.getAttribute('for');
      if (!forAttr) return;

      const input = document.getElementById(forAttr);
      if (!input) return;

      const sizeValue = input.value; // F.eks. "S", "M", "XL"

      // Tjek om dette er en størrelses-label (ikke farve eller serie)
      // Størrelses-inputs har name der indeholder "storrelse"
      if (!input.name.includes('storrelse')) return;

      // Find den variant der matcher: Farve + sizeValue + Serie
      let matchedVariant = allVariants.find(function (v) {
        if (v.option2 !== sizeValue) return false;
        if (activeOption1 && v.option1 !== activeOption1) return false;
        if (activeOption3 && v.option3 !== activeOption3) return false;
        return true;
      });

      // Fallback: match kun størrelse og serie (hvis ingen farve-match)
      if (!matchedVariant && activeOption3) {
        matchedVariant = allVariants.find(function (v) {
          return v.option2 === sizeValue && v.option3 === activeOption3;
        });
      }

      // Fallback: match kun størrelse
      if (!matchedVariant) {
        matchedVariant = allVariants.find(function (v) {
          return v.option2 === sizeValue;
        });
      }

      if (matchedVariant) {
        // Opdater .ea-size-price-sub tekst
        const priceEl = label.querySelector('.ea-size-price-sub');
        if (priceEl) {
          priceEl.textContent = formatPriceNumber(matchedVariant.price);
        }
      }
    });
  }

  // Initialisér en produktsektion
  function initSection(section) {
    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    variantPicker.addEventListener('on:variant:change', function (e) {
      const variant = e.detail.variant;
      if (!variant) return;
      updateAtcButton(variant, section);
      updatePriceLabel(variant, section);
      updateSizeLabels(variant, section);
    });

    // Sæt initial tilstand
    if (variantPicker.variant) {
      updateAtcButton(variantPicker.variant, section);
      updatePriceLabel(variantPicker.variant, section);
      updateSizeLabels(variantPicker.variant, section);
    }
  }

  function init() {
    if (customElements.get('variant-picker')) {
      document.querySelectorAll('.product-info').forEach(function (section) {
        initSection(section);
      });
    } else {
      customElements.whenDefined('variant-picker').then(function () {
        requestAnimationFrame(function () {
          document.querySelectorAll('.product-info').forEach(function (section) {
            initSection(section);
          });
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
